import React, { createContext, useState, useEffect, useContext } from 'react';
import { authAPI } from '../services/api';

/**
 * Authentication Context
 */
export const AuthContext = createContext(null);

/**
 * AuthProvider component to wrap the app and provide auth state
 * @param {Object} props - React props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    /**
     * Load authenticated user information
     */
    const loadUser = async () => {
        try {
            const response = await authAPI.getCurrentUser();
            // Adjust based on actual API response structure (user object might be nested or direct)
            setUser(response.data.user || response.data);
        } catch (error) {
            console.error('Failed to load user', error);

            // Only logout on 401 (truly invalid/expired token)
            // For network errors, timeouts, etc. — keep the session alive using cached data
            if (error.response?.status === 401) {
                logout();
            } else {
                // Try to restore user from localStorage cache
                const cachedUser = localStorage.getItem('user');
                if (cachedUser) {
                    try {
                        setUser(JSON.parse(cachedUser));
                    } catch (e) {
                        logout();
                    }
                } else {
                    logout();
                }
            }
        } finally {
            setLoading(false);
        }
    };

    // Check for token and load user on mount
    useEffect(() => {
        if (token) {
            // Configure axios headers if not already done in interceptors (redundant but safe)
            // Note: Interceptors in api.js handle this, so we just trigger the load
            loadUser();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Login user
     * @param {string} email 
     * @param {string} password 
     * @returns {Promise<{success: boolean, error: string}>}
     */
    const login = async (email, password) => {
        try {
            const response = await authAPI.login({ email, password });
            const { user, token } = response.data;

            // Save to local storage
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            // Update state
            setToken(token);
            setUser(user);

            return { success: true };
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Login failed',
            };
        }
    };

    /**
     * Register new user
     * @param {Object} data - Registration data (email, password, full_name, etc.)
     * @returns {Promise<{success: boolean, error: string}>}
     */
    const register = async (data) => {
        try {
            const response = await authAPI.register(data);
            const { user, token } = response.data;

            // Save to local storage
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            // Update state
            setToken(token);
            setUser(user);

            return { success: true };
        } catch (error) {
            console.error('Registration error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Registration failed',
            };
        }
    };

    /**
     * Login with token (for social auth)
     * @param {string} token - JWT token
     */
    const loginWithToken = async (token) => {
        try {
            localStorage.setItem('token', token);
            setToken(token);

            // Fetch user data with the new token
            const response = await authAPI.getCurrentUser();
            const user = response.data.user || response.data;

            setUser(user);
            localStorage.setItem('user', JSON.stringify(user));

            return { success: true };
        } catch (error) {
            console.error('Login with token failed:', error);
            logout(); // Clean up if invalid
            return {
                success: false,
                error: 'Failed to verify session'
            };
        }
    };

    /**
     * Logout user
     */
    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);

        // Redirect to login if not already there
        if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
        }
    };

    /**
     * Update user state locally
     * @param {Object} userData - New user data
     */
    const updateUser = (userData) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        loginWithToken,
        logout,
        updateUser,
        isAuthenticated: !!user,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
